import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import SiteImageCard from '@/components/seo/SiteImageCard';
import PrimaryColorPicker from '@/components/seo/PrimaryColorPicker';
import { SITE_IMAGE_KEYS } from '@/lib/siteImages';
import { getPrimaryColorForDomain } from '@/lib/siteColors';

const PROMPTS = {
  ogOrthocare: 'Clean professional Open Graph image (1200x630) for an orthopedic equipment rental service in Portugal, soft medical blue tones, mobility equipment, no text, high quality.',
  ogGestos: 'Professional Open Graph image (1200x630) for Gestos Ageis orthopedic store in Lisbon, wheelchair and mobility equipment, green and white medical theme, no text.',
  heroOrthocarePt: 'Cinematic wide photograph of a happy senior couple at home with orthopedic equipment, bright warm natural light, professional medical equipment rental hero image, no text, high quality.',
  heroOrthocarerentPt: 'Senior using a rollator walker on a sunny Portuguese street, cinematic wide shot, warm golden light, medical equipment rental marketing hero image, no text.',
  heroOrthocarerentCom: 'Senior on a mobility scooter in a sunny park, cinematic wide shot, professional medical rental hero image, no text, high quality.',
};

export default function OgImagePreview() {
  const { data: settings = [] } = useQuery({
    queryKey: ['settings', 'v4'],
    queryFn: () => base44.entities.Settings.list('-created_date', 1000),
    staleTime: 1000 * 60 * 5,
  });

  const colorOrthocarePt = getPrimaryColorForDomain(settings, 'orthocare.pt');
  const colorOrthocarerentPt = getPrimaryColorForDomain(settings, 'orthocarerent.pt');
  const colorOrthocarerentCom = getPrimaryColorForDomain(settings, 'orthocarerent.com');
  const colorGestos = getPrimaryColorForDomain(settings, 'gestosageis.pt');

  return (
    <div className="min-h-screen bg-slate-100 p-6">
      <h1 className="text-2xl font-bold text-center mb-2">Imagens do site (OG + Hero)</h1>
      <p className="text-center text-slate-600 mb-8 max-w-2xl mx-auto">
        Altere o URL, carregue uma imagem ou gere com IA e prima <strong>Guardar</strong> — a imagem passa a ser usada no respetivo sítio (hero da landing / meta og:image). É necessária conta de admin.
      </p>

      <PrimaryColorPicker />

      <h2 className="text-lg font-bold text-center mt-12 mb-2">Imagens OG (Open Graph)</h2>
      <p className="text-center text-slate-500 text-sm mb-6">Aparecem nos resultados do Google / partilhas em redes sociais.</p>
      <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8">
        <SiteImageCard
          settingKey={SITE_IMAGE_KEYS.ogOrthocare}
          title="orthocare.pt · /start"
          subtitle="og:image"
          headerBg={colorOrthocarePt}
          previewHeight="h-56"
          defaultPrompt={PROMPTS.ogOrthocare} />
        <SiteImageCard
          settingKey={SITE_IMAGE_KEYS.ogGestos}
          title="gestosageis.pt · /gestosageis"
          subtitle="og:image"
          headerBg={colorGestos}
          previewHeight="h-56"
          defaultPrompt={PROMPTS.ogGestos} />
      </div>

      <h2 className="text-lg font-bold text-center mt-12 mb-2">Imagem de fundo do Hero — por domínio</h2>
      <p className="text-center text-slate-500 text-sm mb-6">Cada domínio <strong>orthocare</strong> tem a sua própria imagem de fundo no hero.</p>
      <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8">
        <SiteImageCard
          settingKey={SITE_IMAGE_KEYS.heroOrthocarePt}
          title="orthocare.pt"
          subtitle="hero background"
          headerBg={colorOrthocarePt}
          previewHeight="h-44"
          defaultPrompt={PROMPTS.heroOrthocarePt} />
        <SiteImageCard
          settingKey={SITE_IMAGE_KEYS.heroOrthocarerentPt}
          title="orthocarerent.pt"
          subtitle="hero background"
          headerBg={colorOrthocarerentPt}
          previewHeight="h-44"
          defaultPrompt={PROMPTS.heroOrthocarerentPt} />
        <SiteImageCard
          settingKey={SITE_IMAGE_KEYS.heroOrthocarerentCom}
          title="orthocarerent.com"
          subtitle="hero background"
          headerBg={colorOrthocarerentCom}
          previewHeight="h-44"
          defaultPrompt={PROMPTS.heroOrthocarerentCom} />
      </div>

      <p className="text-center text-slate-500 mt-8 text-sm">
        Depois de Guardar, pode ser necessário reindexar no Google Search Console para ver a alteração nos resultados.
      </p>
    </div>
  );
}